import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, leadingIcon, trailingIcon, className = '', id, ...rest },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? `inp-${reactId}`;
  const describedBy = error ? `${inputId}-err` : hint ? `${inputId}-hint` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label htmlFor={inputId} className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
          {label}
        </label>
      ) : null}
      <div className={[
        'flex items-center gap-2 rounded border bg-neutral-0 px-3 transition-colors',
        'dark:bg-neutral-900',
        error
          ? 'border-danger focus-within:border-danger'
          : 'border-neutral-300 focus-within:border-brand-500 dark:border-neutral-700',
        'h-10',
      ].join(' ')}>
        {leadingIcon ? <span className="text-neutral-500">{leadingIcon}</span> : null}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={!!error || undefined}
          aria-describedby={describedBy}
          className={[
            'flex-1 bg-transparent text-sm text-neutral-900 placeholder:text-neutral-400 outline-none',
            'dark:text-neutral-100 dark:placeholder:text-neutral-500',
            className,
          ].join(' ')}
          {...rest}
        />
        {trailingIcon ? <span className="text-neutral-500">{trailingIcon}</span> : null}
      </div>
      {error ? (
        <p id={`${inputId}-err`} className="text-xs text-danger">{error}</p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="text-xs text-neutral-500">{hint}</p>
      ) : null}
    </div>
  );
});
